import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { DataGrid, esES } from "@mui/x-data-grid";

import Topbar from "../../../../../../components/topbar/TopBar";
import Header from "../../../../../../components/header/Header";
import Sidebar from "../../../../../../components/sidebar/Sidebar";
import { tokens } from "../../../../../../theme";
import { getRenewVehiclesUseCase } from "../domain/getRenewVehiclesUseCase";
import { approveRenewVehicleUseCase } from "../domain/approveRenewVehicleUseCase";
import { getTokenRefresh } from "../../../../../../helpers/token/refreshToken";
import { RenewVehicleModal } from "./RenewVehicleModal";

const vehicleColumns = [
  { field: "id", headerName: "ID", width: 70 },
  {
    field: "drivers",
    headerName: "Conductor",
    minWidth: 190,
    flex: 1,
    renderCell: ({ row }) =>
      row.drivers
        ?.map(({ name, lastname }) =>
          [name, lastname].filter(Boolean).join(" "),
        )
        .filter(Boolean)
        .join(", ") || "—",
  },
  { field: "brand", headerName: "Marca", minWidth: 100, flex: 1 },
  { field: "model", headerName: "Modelo", minWidth: 100, flex: 1 },
  { field: "bodywork", headerName: "Carrocería", minWidth: 100, flex: 1 },
  { field: "plate", headerName: "Placa", minWidth: 100, flex: 1 },
  { field: "year", headerName: "Año", width: 60 },
  { field: "color", headerName: "Color", minWidth: 80, flex: 1 },
  {
    field: "status",
    headerName: "Estado",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }) =>
      ({ 1: "Activo", 2: "Suspendido", 3: "Eliminado" })[value] || "—",
  },
  {
    field: "action",
    headerName: "Servicio",
    minWidth: 100,
  },
  {
    field: "premium",
    headerName: "Premium",
    width: 80,
    renderCell: ({ value }) => (value ? "Sí" : "No"),
  },
  {
    field: "pendingChange",
    headerName: "Cambios",
    width: 100,
    renderCell: ({ row }) => row.pendingChange?.changes?.length ?? "Heredado",
  },
];

function VehicleActionsMenu({ vehicle, onView, onApprove, busy }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleView = () => {
    setAnchorEl(null);
    onView(vehicle);
  };

  const handleApprove = () => {
    setAnchorEl(null);
    onApprove(vehicle);
  };

  return (
    <>
      <Button
        variant="outlined"
        color="success"
        size="small"
        endIcon={<ArrowDropDownIcon />}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        Opciones
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleView}>Ver</MenuItem>
        <MenuItem onClick={handleApprove} disabled={busy || !vehicle.pendingChange?.requestId}>
          Aprobar
        </MenuItem>
      </Menu>
    </>
  );
}

export function RenewVehicleScreen() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const matchesSM = useMediaQuery(theme.breakpoints.down("sm"));
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [approvalCandidate, setApprovalCandidate] = useState(null);
  const [approving, setApproving] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, message: "", severity: "success" });
  const columns = useMemo(
    () => [
      ...vehicleColumns,
      {
        field: "actions",
        headerName: "Acciones",
        width: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <VehicleActionsMenu vehicle={row} onView={setSelectedVehicle}
            onApprove={setApprovalCandidate} busy={approving} />
        ),
      },
    ],
    [approving],
  );

  async function handleApprove() {
    if (!approvalCandidate || approving) return;
    setApproving(true);
    try {
      await approveRenewVehicleUseCase(getTokenRefresh(), approvalCandidate);
      setVehicles((current) => current.filter((vehicle) => vehicle.id !== approvalCandidate.id));
      setApprovalCandidate(null);
      setFeedback({ open: true, message: "Vehículo aprobado correctamente", severity: "success" });
    } catch (requestError) {
      setFeedback({ open: true, message: requestError.message, severity: "error" });
      if (requestError.code === "CONFLICT") {
        try {
          setVehicles(await getRenewVehiclesUseCase(getTokenRefresh()));
          setApprovalCandidate(null);
        } catch (refreshError) {
          setError("No se pudo actualizar la lista de vehículos");
        }
      }
    } finally {
      setApproving(false);
    }
  }

  useEffect(() => {
    let active = true;
    async function loadRenewVehicles() {
      try {
        const data = await getRenewVehiclesUseCase(getTokenRefresh());
        if (active) setVehicles(data);
      } catch (requestError) {
        if (active)
          setError("No se pudieron cargar los vehículos para renovar");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadRenewVehicles();
    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <CssBaseline />
      <div className="app">
        {!matchesSM && <Sidebar />}
        <main className="content">
          <Topbar />
          <Box m="10px">
            <Header title="RENOVAR VEHÍCULOS" subtitle="" />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            <Box
              mt="10px"
              height="calc(100vh - 150px)"
              minHeight={420}
              sx={{
                "& .MuiDataGrid-root": { border: "none" },
                "& .MuiDataGrid-cell": { borderBottom: "none" },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: colors.blueAccent[700],
                  borderBottom: "none",
                },
                "& .MuiDataGrid-virtualScroller": {
                  backgroundColor: colors.primary[400],
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "none",
                  backgroundColor: colors.blueAccent[700],
                },
              }}
            >
              <DataGrid
                rows={vehicles}
                columns={columns}
                loading={loading}
                localeText={{
                  ...esES.components.MuiDataGrid.defaultProps.localeText,
                  noRowsLabel: "No hay vehículos para renovar",
                }}
              />
            </Box>
          </Box>
          {selectedVehicle && (
            <RenewVehicleModal
              vehicle={selectedVehicle}
              onClose={() => setSelectedVehicle(null)}
            />
          )}
          <Dialog open={Boolean(approvalCandidate)}
            onClose={() => { if (!approving) setApprovalCandidate(null); }} fullWidth maxWidth="xs">
            <DialogTitle>¿Aprobar cambio de vehículo?</DialogTitle>
            <DialogContent>
              <Typography>
                Se aplicarán los cambios propuestos para {approvalCandidate?.plate || "este vehículo"}.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setApprovalCandidate(null)} disabled={approving}>Cancelar</Button>
              <Button variant="contained" color="success" onClick={handleApprove} disabled={approving}>
                {approving ? "Aprobando..." : "Aprobar"}
              </Button>
            </DialogActions>
          </Dialog>
          <Snackbar open={feedback.open} autoHideDuration={4000}
            onClose={() => setFeedback((current) => ({ ...current, open: false }))}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}>
            <Alert variant="filled" severity={feedback.severity}
              onClose={() => setFeedback((current) => ({ ...current, open: false }))}>
              {feedback.message}
            </Alert>
          </Snackbar>
        </main>
      </div>
    </>
  );
}
